import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import logo from '../assets/ChatGPT Image Aug 10, 2026, 06_50_42 PM.png';

const navLinks = [
  { label: 'Home', href: '/' },
  { label: 'Services', href: '#services' },
  { label: 'Domains', href: '/domains' },
  { label: 'Research', href: '#research' },
  { label: 'Projects List', href: '/projects' },
  { label: 'About', href: '#about' },
  { label: 'Contact', href: '#contact' },
];

export default function Navbar({ onExpertClick }) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeLink, setActiveLink] = useState('Home');

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Text color switches based on scroll: white on dark hero, navy on white glass bg
  const linkColor = scrolled
    ? 'text-royal-navy/80 hover:text-royal-gold'
    : 'text-white/90 hover:text-royal-gold';

  const activeLinkColor = scrolled ? 'text-royal-gold' : 'text-royal-gold';
  const wordmarkColor = scrolled ? 'text-royal-navy' : 'text-white';
  const subTextColor = scrolled ? 'text-royal-gold' : 'text-royal-gold-light';
  const hamburgerColor = scrolled ? 'bg-royal-navy' : 'bg-white';

  return (
    <motion.nav
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: 'easeOut' }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${scrolled
          ? 'bg-white/92 backdrop-blur-xl shadow-royal border-b border-parchment-dark'
          : 'bg-transparent'
        }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">

          {/* Logo */}
          <motion.a
            href="#home"
            className="flex items-center gap-3 group flex-shrink-0"
            whileHover={{ scale: 1.03 }}
            transition={{ type: 'spring', stiffness: 300 }}
          >
            <div className="relative">
              <img
                src={logo}
                alt="Connect Students Logo"
                className="h-13 w-13 object-contain drop-shadow-lg"
                style={{ height: '52px', width: '52px' }}
              />
              <div className="absolute inset-0 rounded-full bg-royal-gold/10 group-hover:bg-royal-gold/25 transition-colors duration-300 blur-lg" />
            </div>
            <div className="hidden sm:block">
              <p className={`font-playfair font-bold text-lg leading-tight transition-colors duration-300 ${wordmarkColor}`}>
                Connect Students
              </p>
              <p className={`font-inter text-xs tracking-widest uppercase transition-colors duration-300 ${subTextColor}`}>
                Learn • Connect • Grow
              </p>
            </div>
          </motion.a>

          {/* Desktop Nav Links */}
          <div className="hidden lg:flex items-center gap-7">
            {navLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                onClick={() => setActiveLink(link.label)}
                className={`nav-link pb-1 text-sm font-semibold transition-colors duration-200 ${activeLink === link.label
                    ? `${activeLinkColor} after:w-full`
                    : linkColor
                  }`}
              >
                {link.label}
              </a>
            ))}
          </div>

          {/* CTA Button */}
          <div className="hidden lg:flex items-center gap-4 flex-shrink-0">
            <motion.button
              onClick={onExpertClick}
              className="font-inter font-bold px-6 py-3 rounded-full text-sm transition-all duration-300 inline-block text-center"
              style={
                scrolled
                  ? { background: '#0B1C4E', color: '#fff', boxShadow: '0 4px 16px rgba(11,28,78,0.2)' }
                  : { background: 'linear-gradient(135deg, #C9A84C, #F0D080)', color: '#0B1C4E', boxShadow: '0 4px 20px rgba(201,168,76,0.4)' }
              }
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.97 }}
            >
              Talk to Expert
            </motion.button>
          </div>

          {/* Mobile Menu Toggle */}
          <button
            id="mobile-menu-btn"
            className="lg:hidden flex flex-col gap-1.5 p-2 rounded-lg hover:bg-white/10 transition-colors"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Toggle menu"
          >
            <span className={`block w-6 h-0.5 transition-all duration-300 ${hamburgerColor} ${menuOpen ? 'rotate-45 translate-y-2' : ''}`} />
            <span className={`block w-6 h-0.5 transition-all duration-300 ${hamburgerColor} ${menuOpen ? 'opacity-0' : ''}`} />
            <span className={`block w-6 h-0.5 transition-all duration-300 ${hamburgerColor} ${menuOpen ? '-rotate-45 -translate-y-2' : ''}`} />
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden bg-white/97 backdrop-blur-xl border-t border-parchment-dark overflow-hidden"
            style={{ backgroundColor: 'rgba(255,255,255,0.97)' }}
          >
            <div className="px-6 py-4 flex flex-col gap-4">
              {navLinks.map((link, i) => (
                <motion.a
                  key={link.label}
                  href={link.href}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.06 }}
                  onClick={() => { setMenuOpen(false); setActiveLink(link.label); }}
                  className="font-inter font-semibold text-royal-navy py-2 border-b border-parchment-dark hover:text-royal-gold transition-colors text-sm"
                >
                  {link.label}
                </motion.a>
              ))}
              <button
                className="font-inter font-bold text-center mt-2 block py-3 rounded-full text-sm w-full"
                style={{ background: 'linear-gradient(135deg, #C9A84C, #F0D080)', color: '#0B1C4E' }}
                onClick={() => { setMenuOpen(false); onExpertClick?.(); }}
              >
                Talk to Expert
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
}
